import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  DashboardFiltersFormData,
  DashboardFiltersFormSchema,
} from "@/modules/stochastic/features/DashboardPage/hooks/dashboardFiltersFormSchema";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FetchDashboardDataRequest } from "@/modules/stochastic/features/DashboardPage/api/fetchStochasticDashboardData/types";
import { fetchStochasticDashboardChartsDataAction } from "@/modules/stochastic/features/DashboardPage/slice/StochasticDashboardSlice";
import { RootState } from "@/app/rootReducer";
import { selectIntacctId } from "@/modules/hub/features/UserAppSettings/selectors/userAppSettingsSelectors";
import { useSearchParams } from "react-router-dom";

export function useStochasticDashboard() {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const activeCompanyIntacctId = useSelector(selectIntacctId);

  // Get intacctId from URL params (when coming from Campaign Status page) or use active company
  const intacctId = searchParams.get("intacctId") || activeCompanyIntacctId || "";

  const defaultValues: DashboardFiltersFormData = {
    years: [],
    cities: [],
    trades: [],
  };

  const [activeTab, setActiveTab] = useState<"sales" | "customers" | "review">(
    "sales",
  );

  const { dashboardData, loading, error } = useSelector(
    (state: RootState) => state.stochasticDashboard,
  );

  const dashboardFiltersForm = useForm<DashboardFiltersFormData>({
    resolver: zodResolver(DashboardFiltersFormSchema),
    defaultValues,
    mode: "onChange",
  });

  const buildRequestData = useCallback((): FetchDashboardDataRequest => {
    const formValues = dashboardFiltersForm.getValues();

    return {
      intacctId,
      scope: activeTab,
      years: (formValues.years || []).map((year) => year.name),
      trades: (formValues.trades || []).map((trade) => trade.id),
      cities: (formValues.cities || []).map((city) => city.name),
    };
  }, [dashboardFiltersForm, activeTab, intacctId]);

  const refetchDiscounts = useCallback(() => {
    const requestData = buildRequestData();
    dispatch(fetchStochasticDashboardChartsDataAction(requestData));
  }, [dispatch, buildRequestData]);

  useEffect(() => {
    refetchDiscounts();
  }, [refetchDiscounts]);

  useEffect(() => {
    const subscription = dashboardFiltersForm.watch(() => {
      refetchDiscounts();
    });

    return () => subscription.unsubscribe();
  }, [dashboardFiltersForm, refetchDiscounts]);

  return {
    loading,
    error,
    dashboardData,
    dashboardFiltersForm,
    activeTab,
    setActiveTab,
  };
}
